import type { ReactNode } from "react";
import clsx from "clsx";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useCollapsibleSection } from "@/lib/uiSections";

export interface CollapsibleSectionProps {
  /** Stabiler Schlüssel der Präferenz, z. B. `"projekte.filter"`. */
  sectionKey: string;
  title: ReactNode;
  /** Kurzinfo neben dem Titel – vor allem im eingeklappten Zustand hilfreich („3 Filter aktiv“). */
  summary?: ReactNode;
  /** Zusätzliche Aktionen rechts in der Leiste (z. B. „Zurücksetzen“). */
  actions?: ReactNode;
  /**
   * Auf schmalen Bildschirmen die Leiste ausblenden und den Inhalt immer zeigen –
   * für Bereiche, die dort bereits einen eigenen Umschalter mitbringen.
   */
  mobileAlwaysExpanded?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * Bereich mit persönlicher Ein-/Ausblend-Präferenz (serverseitig je Benutzer).
 * Zeigt eine schmale Leiste mit Titel und Umschalter; der Inhalt bleibt
 * ausgeblendet, bis der Benutzer ihn wieder einblendet.
 */
export function CollapsibleSection({
  sectionKey,
  title,
  summary,
  actions,
  mobileAlwaysExpanded = false,
  className,
  children,
}: CollapsibleSectionProps) {
  const { collapsed, toggle } = useCollapsibleSection(sectionKey);
  const isMobile = useMediaQuery("(max-width: 767px)");
  const forceExpanded = isMobile && mobileAlwaysExpanded;
  const showContent = forceExpanded || !collapsed;
  const contentId = `eh-section-${sectionKey.replace(/[^a-z0-9_-]/gi, "-")}`;

  return (
    <div className={clsx("space-y-3", className)}>
      {forceExpanded ? null : (
        <div
          className={clsx(
            "flex items-center gap-2",
            collapsed
              ? "rounded-ios-lg bg-fill-quaternary/20 px-3 py-1.5 ring-1 ring-separator/30 dark:ring-separator/45"
              : "px-1",
          )}
        >
          <button
            type="button"
            onClick={toggle}
            aria-expanded={!collapsed}
            aria-controls={contentId}
            className="flex min-w-0 flex-1 items-center gap-2 rounded-lg py-1 text-left transition hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
          >
            <ChevronDownIcon
              className={clsx(
                "h-4 w-4 shrink-0 text-label-tertiary transition-transform",
                collapsed && "-rotate-90",
              )}
              aria-hidden
            />
            <span className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
              {title}
            </span>
            {summary ? (
              <span className="min-w-0 truncate text-xs text-label-tertiary">{summary}</span>
            ) : null}
          </button>
          {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
          <button
            type="button"
            onClick={toggle}
            className="shrink-0 rounded-lg px-2 py-1 text-xs font-medium text-label-secondary transition hover:bg-fill-tertiary hover:text-label focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
          >
            {collapsed ? "Einblenden" : "Ausblenden"}
          </button>
        </div>
      )}
      {showContent ? <div id={contentId}>{children}</div> : null}
    </div>
  );
}
